import { useId } from 'react';

interface BrandMarkProps {
  /** Tailwind / className sizing, e.g. "size-8 shrink-0" */
  className?: string;
  /** Accessible label. Ignored when aria-hidden is true. Defaults to "Secure Labs". */
  'aria-label'?: string;
  /** Hide from assistive technology when used decoratively alongside visible text. */
  'aria-hidden'?: true;
}

/**
 * BrandMark — canonical Secure Labs crest mark.
 *
 * Implements the full brand specification from Brand Book V2:
 *  - 6-stop rose-gold linear gradient (135° sweep, userSpaceOnUse coordinates)
 *  - Radial sheen overlay (28% cx, 22% cy, 48% r) for metallic depth
 *  - Correct 3-path crest geometry (no extra waypoints)
 *
 * Use this component instead of inline SVG in every layout/screen.
 * The useId() hook ensures gradient IDs are unique when multiple marks
 * appear on the same rendered page.
 */
export function BrandMark({
  className,
  'aria-label': ariaLabel = 'Secure Labs',
  'aria-hidden': ariaHidden,
}: BrandMarkProps) {
  const uid = useId().replace(/:/g, '');
  const linId = `${uid}-lin`;
  const radId = `${uid}-rad`;

  const paths = [
    'M 283.935 363.791 L 79.050 363.791 L 188.450 67.797 L 411.543 69.514 L 519.185 363.791 L 314.314 363.791 L 314.314 394.390 L 559.977 395.056 L 430.679 39.994 L 166.739 39.994 L 39.990 392.440 L 283.935 393.670 Z',
    'M 199.721 86.005 L 230.973 85.147 L 144.170 319.524 L 283.935 319.524 L 283.935 349.904 L 103.364 348.173 Z',
    'M 264.827 85.133 L 399.386 86.005 L 432.368 171.077 L 401.989 171.064 L 381.151 113.767 L 283.063 113.795 L 246.605 214.486 L 446.256 214.486 L 494.871 348.173 L 314.314 348.173 L 314.314 319.524 L 455.810 319.524 L 428.034 243.993 L 207.517 243.134 Z',
  ] as const;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 600 400"
      className={className}
      aria-hidden={ariaHidden}
      aria-label={ariaHidden ? undefined : ariaLabel}
      role={ariaHidden ? 'presentation' : 'img'}
    >
      <defs>
        {/* 6-stop rose-gold gradient — Brand Book V2 § 2.1 */}
        <linearGradient id={linId} x1="40" y1="40" x2="560" y2="395" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#B87745" />
          <stop offset="20%" stopColor="#D69A5E" />
          <stop offset="45%" stopColor="#F4C888" />
          <stop offset="65%" stopColor="#E6B070" />
          <stop offset="85%" stopColor="#C08860" />
          <stop offset="100%" stopColor="#8A5A30" />
        </linearGradient>
        {/* Radial sheen for metallic depth */}
        <radialGradient id={radId} cx="28%" cy="22%" r="48%" gradientUnits="objectBoundingBox">
          <stop offset="0%" stopColor="#FFF2D8" stopOpacity="0.3" />
          <stop offset="50%" stopColor="#F4C888" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#F4C888" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Primary fill — rose-gold gradient */}
      <g fill={`url(#${linId})`}>
        {paths.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </g>
      {/* Sheen overlay */}
      <g fill={`url(#${radId})`} aria-hidden>
        {paths.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </g>
    </svg>
  );
}
